import { Link } from "react-router-dom";
import { MessageSquareText, ArrowRight } from "lucide-react";
import { GALLERY, SMS_HREF } from "@/lib/site";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { EditableText } from "@/lib/editMode";
import useWorkJobs from "@/hooks/useWorkJobs";

export default function Gallery() {
    const { jobs } = useWorkJobs();
    useSEO({
        title: "Recent Work — Mobile Mechanic Jobs",
        description:
            "Real jobs done by Limit Break Performance — brakes, suspension, intakes, engine work — completed mobile across Hampton Roads, Virginia.",
        image: GALLERY[0]?.image,
    });

    return (
        <div data-testid="gallery-page">
            <Breadcrumb section="Work" />
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-16 sm:pb-20 lg:pb-24">
                    <EditableText
                        path="gallery.page.eyebrow"
                        defaultValue="Real jobs, real cars"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="gallery.page.title"
                        defaultValue="The Work"
                        as="h1"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="gallery-hero-title"
                    />
                    <EditableText
                        path="gallery.page.subtitle"
                        defaultValue="Tap any job to see the full breakdown — what was done, what it costs at the dealership, and what we charge to do it right at your driveway."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />
                </div>
            </section>

            <section className="bg-zinc-950">
                <div className="lbp-container py-14 sm:py-20">
                    <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {jobs.map((item, i) => (
                            <Link
                                to={`/work/${item.slug}`}
                                key={item.slug}
                                className="group relative overflow-hidden rounded-sm border border-white/10 bg-zinc-900 aspect-[4/5] block"
                                data-testid={`gallery-item-${i}`}
                            >
                                <img
                                    src={item.cover || item.image}
                                    alt={item.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                                <figcaption className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                                    <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold">
                                        Job #{String(i + 1).padStart(2, "0")} · <EditableText path={`gallery_items.${item.slug}.serviceType`} defaultValue={item.serviceType} as="span" />
                                    </div>
                                    <EditableText
                                        path={`gallery_items.${item.slug}.title`}
                                        defaultValue={item.title}
                                        as="h3"
                                        className="mt-1 break-words font-display font-bold text-white text-lg sm:text-xl leading-snug"
                                    />
                                    <EditableText
                                        path={`gallery_items.${item.slug}.caption`}
                                        defaultValue={item.caption}
                                        multiline
                                        as="p"
                                        className="mt-2 line-clamp-3 break-words text-sm text-zinc-300 leading-relaxed"
                                    />
                                    <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white font-semibold">
                                        See full breakdown <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </figcaption>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-black border-t border-white/10">
                <div className="lbp-container py-14 sm:py-20 text-center">
                    <EditableText
                        path="gallery.final_cta.title"
                        defaultValue="Your Car Next?"
                        as="h2"
                        className="font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl"
                    />
                    <a
                        href={SMS_HREF}
                        data-testid="gallery-cta-text"
                        className="mt-6 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                    >
                        <MessageSquareText className="w-5 h-5" />
                        <EditableText path="gallery.final_cta.btn" defaultValue="Text Now For A Quote" as="span" />
                    </a>
                </div>
            </section>
        </div>
    );
}
